import React from 'react'
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
} from '@react-pdf/renderer'
import medunitedLogo from '../../../assets/images/medunited.png'
import kimsLogo from '../../../assets/images/kimslogo.png'
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
      fontWeight: 'bold',
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-italic-webfont.ttf',
      fontStyle: 'italic',
    },
  ],
})

interface Props {
  formValues: any
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#FFFFFF',
    fontFamily: 'Roboto',
    fontSize: 9,
    padding: 20,
  },
  logoContainer: {
    marginBottom: 10,
  },
  header: {
    fontSize: 13,
    fontWeight: 'bold',
  },
  subHeader: {
    fontSize: 11,
  },
  section: {
    paddingBottom: 15,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginBottom: 5,
  },
  table: {
    borderWidth: 1,
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },
  tableHeader: {
    flex: 1,
    backgroundColor: '#f2f2f2',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    textAlign: 'center',
    padding: 4,
  },
  tableCell: {
    flex: 1,
    padding: 4,
    textAlign: 'center',
  },
  signatureRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  bold: {
    fontWeight: 'bold',
    marginBottom: 6,
    marginTop: 6,
  },
  departmentHeader: {
    fontSize: 10,
    marginBottom: 8,
    fontFamily: 'Roboto',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  logo: {
    width: 155,
    height: 40,
    paddingRight: 10,
  },
  textContainer: {
    // flexDirection: 'column',
    width: '100%',
    justifyContent: 'space-around',
  },
  label: {
    width: 120, // Adjust width for alignment
    fontWeight: 'bold',
  },

  sectionOne: {
    flex: 1,
    padding: 5,
    gap: 2,
  },
  labelPhysical: {
    width: 50,
    fontWeight: 'bold',
  },
  sectionPhysical: {
    flex: '0 1 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  signatureImg: {
    width: 60,
    height: 20,
  },
  sectionEnd: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  headingContainer: {
    fontSize: 14,
    fontWeight: 'bold',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  sectionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  addressText: {
    fontSize: 11,
    lineHeight: 1.5,
    maxWidth: '100%',
    textAlign: 'center',
  },
  symptomsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '5px',
  },
  symptomsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '15px',
  },
  followUpText: {
    marginTop: '20px',
  },
  mainContainerView: {
    display: 'flex',
    flexDirection: 'row',
  },
  headerDivider: {
    width: '100%',
    height: 1,
    backgroundColor: 'black',
    marginBottom: 6,
  },
})

const PatientDischargeSummaryPdf: React.FC<Props> = ({ formValues }) => (
  <Document>
    <Page size='A4' style={styles.page} wrap>
      <View style={styles.mainContainerView}>
        <View style={styles.logoContainer}>
          {formValues?.mrNo?.startsWith('MUH') ? (
            <Image source={medunitedLogo} style={styles.logo} />
          ) : formValues?.mrNo?.startsWith('KIMH') ? (
            <Image source={kimsLogo} style={styles.logo} />
          ) : (
            <Image source={medunitedLogo} style={styles.logo} />
          )}
        </View>
        <View style={styles.textContainer}>
          <Text style={styles.headingContainer}>
            {formValues?.mrNo?.startsWith('KIMH')
              ? 'KIMS GENERAL & SUPER SPECIALTY HOSPITAL'
              : 'MEDUNITED HOSPITALS'}
          </Text>
          <View>
            <Text style={styles.addressText}>
              {formValues?.locationAddress}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.headerDivider}></View>

      <View style={styles.sectionContainer}>
        <View style={styles.sectionOne}>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.label}>Name :</Text>
            <Text>{formValues?.name}</Text>
          </View>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.label}>Age/Gender :</Text>
            <Text>{formValues?.ageGender}</Text>
          </View>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.label}>Location :</Text>
            <Text style={{ width: 120 }}>{formValues?.locationAddress}</Text>
          </View>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.label}>Doctor :</Text>
            <Text>{formValues?.doctor}</Text>
          </View>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.label}>Department :</Text>
            <Text>{formValues?.department}</Text>
          </View>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.label}>Rate Plan :</Text>
            <Text>{formValues?.ratePlan}</Text>
          </View>
        </View>

        <View style={styles.sectionOne}>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.label}>MR No :</Text>
            <Text>{formValues?.mrNo}</Text>
          </View>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.label}>Visit ID :</Text>
            <Text>{formValues?.visitId}</Text>
          </View>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.label}>Admission Date :</Text>
            <Text>{formValues?.admissionDate}</Text>
          </View>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.label}>Discharge Date :</Text>
            <Text>{formValues?.dischargeDate}</Text>
          </View>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.label}>Ward/Bed :</Text>
            <Text>{formValues?.wardBed}</Text>
          </View>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.label}>Source :</Text>
            <Text>{formValues?.source}</Text>
          </View>
        </View>
      </View>

      {/* <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <View style={styles.section}>
          <Text>Name: {formValues?.name}</Text>
          <Text>Age/Gender: {formValues?.ageGender}</Text>
          <Text>Location: {formValues?.locationAddress}</Text>
          <Text>Doctor: {formValues?.doctor}</Text>
          <Text>Department: {formValues?.department}</Text>
          <Text>Rate Plan: {formValues?.ratePlan}</Text>
        </View>

        <View style={styles.section}>
          <Text>MR No: {formValues?.mrNo}</Text>
          <Text>Visit ID: {formValues?.visitId}</Text>
          <Text>Admission Date: {formValues?.admissionDate}</Text>
          <Text>Discharge Date: {formValues?.dischargeDate}</Text>
          <Text>Ward/Bed: {formValues?.wardBed}</Text>
          <Text>Source: {formValues?.source}</Text>
        </View>
      </View> */}

      <Text style={styles.departmentHeader}>Discharge Summary</Text>

      <View style={styles.section}>
        <Text style={styles.bold}>FINAL DIAGNOSIS :</Text>
        <Text>{formValues?.finalDiagnosis}</Text>

        <Text style={styles.bold}>HISTORY :</Text>
        <Text>{formValues?.history}</Text>

        <Text style={styles.bold}>INGESTIONS DONE :</Text>
        <Text>{formValues?.investigationsDone}</Text>

        <Text style={styles.bold}>TREATMENT GIVEN :</Text>
        <Text>{formValues?.treatmentGiven}</Text>

        <Text style={styles.bold}>PROGESS IN HOSPITAL :</Text>
        <Text>{formValues?.progressInHospital}</Text>

        <Text style={styles.bold}>TREATMENT ADVISED :</Text>
        <Text>{formValues?.treatmentAdvised}</Text>
      </View>
      <Text style={styles.bold}>PHYSICAL EXAMINATION</Text>
      <View style={styles.sectionPhysical}>
        <View style={{ flexDirection: 'row' }}>
          <Text style={styles.labelPhysical}>O/E : </Text>
          <Text>{formValues?.oe}</Text>
        </View>
        <View style={{ flexDirection: 'row' }}>
          <Text style={styles.labelPhysical}>BP : </Text>
          <Text>{formValues?.bp}</Text>
        </View>
        <View style={{ flexDirection: 'row' }}>
          <Text style={styles.labelPhysical}>PR : </Text>
          <Text>{formValues?.pr}</Text>
        </View>
        <View style={{ flexDirection: 'row' }}>
          <Text style={styles.labelPhysical}>SPO2 : </Text>
          <Text>{formValues?.spo2}</Text>
        </View>
        <View style={{ flexDirection: 'row' }}>
          <Text style={styles.labelPhysical}>Temp : </Text>
          <Text>{formValues?.temp}</Text>
        </View>
        <View style={{ flexDirection: 'row' }}>
          <Text style={styles.labelPhysical}>CVS : </Text>
          <Text>{formValues?.cvs}</Text>
        </View>
        <View style={{ flexDirection: 'row' }}>
          <Text style={styles.labelPhysical}>RS : </Text>
          <Text>{formValues?.rs}</Text>
        </View>
        <View style={{ flexDirection: 'row' }}>
          <Text style={styles.labelPhysical}>PA : </Text>
          <Text>{formValues?.pa}</Text>
        </View>
        <View style={{ flexDirection: 'row' }}>
          <Text style={styles.labelPhysical}>CNS : </Text>
          <Text>{formValues?.cns}</Text>
        </View>
      </View>

      <Text style={styles.bold}>MEDICATIONS :</Text>
      {formValues?.medications?.length > 0 && (
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={styles.tableHeader}>S.No</Text>
            <Text style={styles.tableHeader}>Medicine</Text>
            <Text style={styles.tableHeader}>Morning</Text>
            <Text style={styles.tableHeader}>Afternoon</Text>
            <Text style={styles.tableHeader}>Evening</Text>
            <Text style={styles.tableHeader}>No. of Days</Text>
            <Text style={styles.tableHeader}>Remarks</Text>
          </View>

          {formValues?.medications?.map((medication: any) => (
            <View key={medication.sno} style={styles.tableRow}>
              <Text style={styles.tableCell}>{medication?.sno || '-'}</Text>
              <Text style={styles.tableCell}>
                {medication?.medicine || '-'}
              </Text>
              <Text style={styles.tableCell}>{medication?.morning || '-'}</Text>
              <Text style={styles.tableCell}>
                {medication?.afternoon || '-'}
              </Text>
              <Text style={styles.tableCell}>{medication?.evening || '-'}</Text>
              <Text style={styles.tableCell}>
                {medication?.noOfDays || '-'}
              </Text>
              <Text style={styles.tableCell}>{medication?.remarks || '-'}</Text>
            </View>
          ))}
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.bold}>PREVENTIVE CARE :</Text>
        <Text>{formValues?.preventiveCare}</Text>

        <Text style={styles.bold}>DIET :</Text>
        <Text>{formValues?.diet}</Text>

        <Text style={styles.followUpText}>
          <Text style={styles.bold}>FOLLOW UP/ REVIEW :</Text>
          {` Follow-up after ${formValues?.daysAfter} days in ${formValues?.department} OPD with all medical documents.`}
        </Text>
      </View>

      <View style={styles.symptomsSection}>
        <Text style={styles.bold}>
          Symptoms of Emergency : (If Observed, please report to Emergency Dept.
          (Casualty))
        </Text>
        <View style={styles.symptomsContainer}>
          {formValues?.symptoms?.map((symptom: string, index: number) => (
            <Text key={index}>{`${index + 1}. ${symptom}`}</Text>
          ))}
        </View>
      </View>

      <View style={styles.sectionContainer}>
        <View style={styles.sectionEnd}>
          <Text>Prepared By : {formValues?.preparedByDoctor}</Text>
          <Text>Date : {formValues?.preparedByDate}</Text>
          <Text>
            Signature :{' '}
            {formValues?.preparedByDoctorSignature && (
              <Image
                style={styles.signatureImg}
                src={formValues?.preparedByDoctorSignature}
              />
            )}
          </Text>
        </View>
        <View style={styles.sectionEnd}>
          <Text>Treating Doctor : {formValues?.treatingDoctor}</Text>
          <Text>Reg No : {formValues?.treatingDoctorRegNo}</Text>
          <Text>
            Signature :{' '}
            {formValues?.treatingDoctorSignature && (
              <Image
                style={styles.signatureImg}
                src={formValues?.treatingDoctorSignature}
              />
            )}
          </Text>
        </View>
      </View>
      {/* <Text
        render={({ pageNumber, totalPages }) =>
          `Page ${pageNumber} of ${totalPages}`
        }
      /> */}
    </Page>
  </Document>
)

export default PatientDischargeSummaryPdf
